import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'helpers/usePermission';
import { useAppDispatch, useAppSelector } from 'store';
import ActionBtn from 'components/buttons/ActionBtn';
import EmptyState from 'components/EmptyState/EmptyState';
import { handleApiError } from 'helpers/handleApiError';
import { useBackOfficeRealtime } from 'hooks/useBackOfficeRealtime';
import {
  browserSessionStorage,
  parsingStatusDefaultLabel,
  parsingStatusLabelKey,
  writeStoredParsingRun,
} from 'store/gameCenter/parsing/progress';
import {
  applyParsingProgress,
  launchFullGameParsing,
} from 'store/gameCenter/parsing/slice';
import styles from './Parsing.module.scss';

const ParsingList: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const permissions = usePermissions('game_center_parsing');
  const run = useAppSelector((state) => state.gameParsing.run);
  const isLaunching = useAppSelector((state) => state.gameParsing.isLaunching);
  const canParse = permissions?.includes('update') ?? false;
  const { subscribe, subscribeResource } = useBackOfficeRealtime();

  useEffect(() => {
    return subscribe('gameParsingProgress', (data) => {
      dispatch(applyParsingProgress(data));
    });
  }, [dispatch, subscribe]);

  useEffect(() => {
    if (!run?.runId) {
      return undefined;
    }
    return subscribeResource('gameParsing', run.runId);
  }, [run?.runId, subscribeResource]);

  const handleParse = useCallback(async () => {
    if (!canParse) {
      return;
    }

    const result = await dispatch(launchFullGameParsing());
    if (launchFullGameParsing.rejected.match(result)) {
      handleApiError({
        error: result.payload ?? result.error,
        t,
        dispatch,
      });
    }
  }, [canParse, dispatch, t]);

  useEffect(() => {
    writeStoredParsingRun(browserSessionStorage(), run);
  }, [run]);

  return (
    <div className="page-list--wo-filter">
      <div className="page-list-header">
        <div className="page-list-header--box">
          <ActionBtn
            txt={t('parseGames')}
            isWithText
            isLoad={isLaunching}
            disabled={!canParse || isLaunching}
            handleClick={() => {
              void handleParse();
            }}
          />
        </div>
      </div>

      {!run ? (
        <EmptyState message={t('parsingEmpty')} />
      ) : (
        <>
          <div className={`game-table-wrap ${styles.tableWrap}`}>
            <table className="game-table">
              <thead>
                <tr>
                  <th>{t('parsingRunId')}</th>
                  <th>{t('status')}</th>
                  <th>{t('parsingTotal')}</th>
                  <th>{t('parsingNew')}</th>
                  <th>{t('parsingExisting')}</th>
                  <th>{t('parsingRequestFailures')}</th>
                  <th>{t('parsingGameFailures')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{run.runId}</td>
                  <td>
                    <span
                      className={`${styles.status} ${styles.runStatus}`}
                      data-status={run.status}
                      data-outcome={run.outcome || ''}
                    >
                      {t(parsingStatusLabelKey(run), {
                        defaultValue: parsingStatusDefaultLabel(run),
                      })}
                    </span>
                  </td>
                  <td>{run.total}</td>
                  <td>{run.new}</td>
                  <td>{run.existing}</td>
                  <td>{run.requestFailures}</td>
                  <td>{run.gameFailures}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {run.providers ? (
            <div className={`game-table-wrap ${styles.providerTable}`}>
              <table className="game-table">
                <thead>
                  <tr>
                    <th>{t('parsingProvider')}</th>
                    <th>{t('status')}</th>
                    <th>{t('parsingCurrenciesCount')}</th>
                    <th>{t('parsingGamesCount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {run.providers.length === 0 ? (
                    <tr>
                      <td colSpan={4}>{t('parsingProvidersEmpty')}</td>
                    </tr>
                  ) : (
                    run.providers.map((provider) => (
                      <tr key={provider.providerId}>
                        <td>{provider.providerName || provider.providerId}</td>
                        <td>
                          <span className={styles.status} data-status={provider.status}>
                            {t(
                              provider.status === 'active'
                                ? 'parsingProviderActive'
                                : 'parsingProviderInactive',
                            )}
                          </span>
                        </td>
                        <td>{provider.currenciesCount}</td>
                        <td>{provider.gamesCount}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default ParsingList;
